import {
  useCallback, useMemo, useState,
} from 'react';
import { toast } from 'react-toastify';
import { handleErrorMessages } from 'src/helper/utils';
import { useAppDispatch } from 'src/hooks/redux-hook';
import {
  workOrderActionAPI,
} from 'src/redux/job-details/api';
import { updateJobDetailsData } from 'src/redux/job-details/slice';

type ActionHookProp = {
  abilities: Array<{
    label: string;
    value: string;
  }>;
  workOrderId: string;
};

export type ActionButtonType = {
  slug: string[];
  label: string;
  svg: string;
  className: string;
  onClick: () => void;
  isDisabled: boolean;
};

const actionApiRoute:Record<string, string> = {
  closed: '/actions/de-escalate',
  cancel: '/actions/cancel',
  schedule: '/actions/schedule',
  reschedule: '/actions/reschedule',
  scheduling_in_progress: '/actions/request-for-schedule',
  start_work: '/actions/start-work',
  quality_check: '/actions/quality-check',
  pause: '/actions/pause',
  resume: '/actions/resume',
  work_completed: '/actions/process',
  create_quote: '/quotes/actions/create-quote',
  submit_quote: '/quotes/actions/submit-quote',
  kick_back_to_pro: '/quotes/actions/kick-back-to-pro',
  approve_quote: '/quotes/actions/approve-quote',
  reject_quote: '/quotes/actions/reject-quote',
  restore_quote: '/quotes/actions/restore-quote',
  sent_for_customer_approval: '/quotes/actions/sent-for-customer-approval',
  mark_as_invoiced: '/invoices/actions/mark-as-invoiced',
  mark_as_paid: '/invoices/actions/mark-as-paid',
};

const useWorkOrderAction = (props: ActionHookProp) => {
  const { abilities, workOrderId } = props;
  const dispatch = useAppDispatch();

  const [loadingState, setLoadingState] = useState<Record<string, boolean>>({});

  const checkIsActionDisabled = useCallback(
    (slugs: string[]) => slugs?.some(
      (slug) => !(abilities || [])?.some((ability) => ability?.value === slug),
    ),
    [abilities],
  );

  const executeWorkOrderAPI = useCallback(
    (slug: string) => {
      if (workOrderId && actionApiRoute?.[slug]) {
        setLoadingState((prev) => ({ ...(prev || {}), [slug]: true }));
        dispatch(workOrderActionAPI(workOrderId, actionApiRoute?.[slug])({}))
          .unwrap()
          .then((res) => {
            if (res) {
              dispatch(updateJobDetailsData({ workOrderData: res }));
            }
            toast('Work Order Updated Successfully', { type: 'success', theme: 'colored' });
            setLoadingState((prev) => ({ ...(prev || {}), [slug]: false }));
          })
          .catch((error) => {
            handleErrorMessages(
              {
                errors: error?.data?.errors || error?.message,
              },
              (msg) => toast(msg, { type: 'error', theme: 'colored' }),
            );
            setLoadingState((prev) => ({ ...(prev || {}), [slug]: false }));
          });
      }
    },
    [dispatch, workOrderId],
  );

  const actions:Array<ActionButtonType> = useMemo(
    () => [
      {
        slug: ['closed'],
        label: 'De-escalate',
        svg: '',
        className: 'action-button',
        onClick: () => executeWorkOrderAPI('closed'),
        isDisabled: checkIsActionDisabled(['closed']),
      },
      {
        slug: ['cancel'],
        label: 'Cancel',
        svg: '',
        className: 'action-button',
        onClick: () => executeWorkOrderAPI('cancel'),
        isDisabled: checkIsActionDisabled(['cancel']),
      },
      {
        slug: ['schedule'],
        label: 'Schedule',
        svg: '',
        className: 'action-button',
        onClick: () => executeWorkOrderAPI('schedule'),
        isDisabled: checkIsActionDisabled(['schedule']),
      },
      {
        slug: ['reschedule'],
        label: 'Reschedule',
        svg: '',
        className: 'action-button',
        onClick: () => executeWorkOrderAPI('reschedule'),
        isDisabled: checkIsActionDisabled(['reschedule']),
      },
      {
        slug: ['scheduling_in_progress'],
        label: 'Request for Schedule',
        svg: '',
        className: 'action-button',
        onClick: () => executeWorkOrderAPI('scheduling_in_progress'),
        isDisabled: checkIsActionDisabled(['scheduling_in_progress']),
      },
      {
        slug: ['start_work'],
        label: 'Start Work',
        svg: '',
        className: 'action-button',
        onClick: () => executeWorkOrderAPI('start_work'),
        isDisabled: checkIsActionDisabled(['start_work']),
      },
      {
        slug: ['create_quote'],
        label: 'Create Quote',
        svg: '',
        className: 'action-button',
        onClick: () => executeWorkOrderAPI('create_quote'),
        isDisabled: checkIsActionDisabled(['create_quote']),
      },
      {
        slug: ['submit_quote'],
        label: 'Submit Quote',
        svg: '',
        className: 'action-button',
        onClick: () => executeWorkOrderAPI('submit_quote'),
        isDisabled: checkIsActionDisabled(['submit_quote']),
      },
      {
        slug: ['kick_back_to_pro'],
        label: 'Kick Back To Pro',
        svg: '',
        className: 'action-button',
        onClick: () => executeWorkOrderAPI('kick_back_to_pro'),
        isDisabled: checkIsActionDisabled(['kick_back_to_pro']),
      },
      {
        slug: ['approve_quote'],
        label: 'Approve Quote',
        svg: '',
        className: 'action-button',
        onClick: () => executeWorkOrderAPI('approve_quote'),
        isDisabled: checkIsActionDisabled(['approve_quote']),
      },
      {
        slug: ['reject_quote'],
        label: 'Reject Quote',
        svg: '',
        className: 'action-button',
        onClick: () => executeWorkOrderAPI('reject_quote'),
        isDisabled: checkIsActionDisabled(['reject_quote']),
      },
      {
        slug: ['restore_quote'],
        label: 'Restore Quote',
        svg: '',
        className: 'action-button',
        onClick: () => executeWorkOrderAPI('restore_quote'),
        isDisabled: checkIsActionDisabled(['restore_quote']),
      },
      {
        slug: ['sent_for_customer_approval'],
        label: 'Sent For Customer Approval',
        svg: '',
        className: 'action-button',
        onClick: () => executeWorkOrderAPI('sent_for_customer_approval'),
        isDisabled: checkIsActionDisabled(['sent_for_customer_approval']),
      },
      {
        slug: ['quality_check'],
        label: 'Quality Check',
        svg: '',
        className: 'action-button',
        onClick: () => executeWorkOrderAPI('quality_check'),
        isDisabled: checkIsActionDisabled(['quality_check']),
      },
      {
        slug: ['pause'],
        label: 'Pause',
        svg: '',
        className: 'action-button',
        onClick: () => executeWorkOrderAPI('pause'),
        isDisabled: checkIsActionDisabled(['pause']),
      },
      {
        slug: ['resume'],
        label: 'Resume',
        svg: '',
        className: 'action-button',
        onClick: () => executeWorkOrderAPI('resume'),
        isDisabled: checkIsActionDisabled(['resume']),
      },
      {
        slug: ['work_completed'],
        label: 'Process Work Order',
        svg: '',
        className: 'action-button',
        onClick: () => executeWorkOrderAPI('work_completed'),
        isDisabled: checkIsActionDisabled(['work_completed']),
      },
      {
        slug: ['mark_as_invoiced'],
        label: 'Mark As Invoiced',
        svg: '',
        className: 'action-button',
        onClick: () => executeWorkOrderAPI('mark_as_invoiced'),
        isDisabled: checkIsActionDisabled(['mark_as_invoiced']),
      },
      {
        slug: ['mark_as_paid'],
        label: 'Mark As paid',
        svg: '',
        className: 'action-button',
        onClick: () => executeWorkOrderAPI('mark_as_paid'),
        isDisabled: checkIsActionDisabled(['mark_as_paid']),
      },
    ],
    [
      executeWorkOrderAPI,
      checkIsActionDisabled,
    ],
  );

  return {
    actions,
    loadingState,
  };
};

export default useWorkOrderAction;
